import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab
} from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import useDict from '../../../components/_hooks/useDict';
import MultiSelect from '../../../_ui/MultiSelect/MultiSelectComponent';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { useTranslation } from 'react-i18next';
import { addMenu, updateMenu } from '../MenuDucks';
import { useDispatch } from 'react-redux';
import { menuAddEditColumns } from './MenuAddEditColumns';
import StaticTablePagination from '../../../_ui/Table/StaticTablePaginated';
import useSimpleModal from '../../../components/_hooks/useSimpleModal';
import MenuRecentListDialog from './MenuRecentList';
import { addWorkingDays, formatDateToAPI, getInitialMenuData } from '../MenuUtils';

export default function AddMenuDialog({ open, close,  serviceProviderId, initialData }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const recentMenuDialog = useSimpleModal();
  const ageGroupsRaw = useDict('gu_age_group') || [];
  const ageGroups = ageGroupsRaw
    .filter(item => item.View === 'true')
    .map(item => ({ code: item.code, name: item.ru_name || item.kk_name }));
  const initialMenuData = getInitialMenuData(initialData);
  const [menuId, setMenuId] = useState(initialMenuData.menuId);
  const [startDate, setStartDate] = useState(initialMenuData.startDate);
  const [endDate, setEndDate] = useState(initialMenuData.endDate);
  const [selectedAgeGroups, setSelectedAgeGroups] = useState(initialMenuData.selectedAgeGroups);
  const [mealTypes, setMealTypes] = useState(initialMenuData.mealTypes);
  const [activeDay, setActiveDay] = useState(0);
  const emptyMeals = {
    breakfast: [],
    secondBreakfast: [],
    lunch: [],
    snack: [],
    dinner: [],
    lateDinner: []
  };
  const [mealsByDay, setMealsByDay] = useState(initialMenuData.mealsByDay);
  const [activeMealTab, setActiveMealTab] = useState(0);
  const mealsLabels = {
    breakfast: t('menu_breakfast'),
    secondBreakfast: t('menu_secondBreakfast'),
    lunch: t('menu_lunch'),
    snack: t('menu_snack'),
    dinner: t('menu_dinner'),
    lateDinner: t('menu_lateDinner')
  };

  const mealKeys = Object.keys(emptyMeals).filter(key => {
    if (key === 'dinner' && !mealTypes.dinner) return false;
    if (key === 'lateDinner' && !mealTypes.lateDinner) return false;
    return true;
  });

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (startDate) {
      setEndDate(addWorkingDays(startDate, 10));
    }
  }, [startDate]);

  const days = React.useMemo(() => {
    if (!startDate) return [];

    const workingDays = [];
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    let daysCount = 0;
    while (daysCount < 10) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays.push(new Date(currentDate));
        daysCount++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return workingDays;
  }, [startDate]);

  const formatDayLabel = date =>
    `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;

  const handleMealCheckboxChange = event => {
    const { name, checked } = event.target;
    setMealTypes(prev => {
      const newMealTypes = { ...prev, [name]: checked };
      if (!checked) {
        const newMealsByDay = mealsByDay.map(day => ({
          ...day,
          [name]: []
        }));
        setMealsByDay(newMealsByDay);
      }

      return newMealTypes;
    });
  };


  const handleAddDish = () => {
    const newMeals = [...mealsByDay];
    const dayMeals = { ...newMeals[activeDay] };

    dayMeals[mealKeys[activeMealTab]] = [...dayMeals[mealKeys[activeMealTab]], ''];

    newMeals[activeDay] = dayMeals;
    setMealsByDay(newMeals);
  };

  const handleSave = () => {
    const menuItems = mealsByDay.map(d => ({
      breakfast: { dish: d.breakfast },
      secondBreakfast: { dish: d.secondBreakfast },
      lunch: { dish: d.lunch },
      snack: { dish: d.snack },
      dinner: { dish: d.dinner },
      lateDinner: { dish: d.lateDinner }
    }));

    const dataToSend = {
      ...(menuId && { id: menuId }),
      startDate: formatDateToAPI(startDate),
      endDate: formatDateToAPI(endDate),
      serviceProviderId,
      menuItems,
      ...(menuId
          ? { ageGroupId: Number(selectedAgeGroups) }
          : { ageGroupIds: selectedAgeGroups }
      )    };
    if (menuId) {
      dispatch(updateMenu(dataToSend, close));
    } else {
      dispatch(addMenu(dataToSend, close));
    }
  };

  const tableData = mealsByDay.map((dayMeals, index) => ({
    rowNumber: index + 1,
    dayLabel: days[index] ? formatDayLabel(days[index]) : '',
    ...dayMeals,
  }));


  const currentMeals = mealsByDay[activeDay];
  const isEditing = !!menuId;
  const isAllDaysFilled = mealsByDay.length === 10 && mealsByDay.every(day => {
    return Object.keys(day).some(mealKey => {
      if ((mealKey === 'dinner' && !mealTypes.dinner) || (mealKey === 'lateDinner' && !mealTypes.lateDinner)) {
        return false;
      }
      return day[mealKey].some(dish => dish.trim() !== '');
    });
  });

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="lg">
      <DialogTitle>
        {isEditing ? t('menuPage_editMenuTitle') : t('menuPage_addMenuTitle')}
      </DialogTitle>

      <DialogContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography>{t('menuPage_startDateOutputMenu')}</Typography>
          <TextField
            type="date"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            style={{ width: 600 }}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography>{t('menuPage_endDateOutputMenu')}</Typography>
          <TextField
            type="date"
            InputLabelProps={{ shrink: true }}
            disabled
            value={endDate}
            style={{ width: 600 }}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          <Typography>{t('menuPage_ageGroups')}</Typography>
          <Box style={{ width: 600 }}>
            <MultiSelect
              withoutForm
              name="ageGroups"
              multiple={!isEditing}
              placeholder={t('menuPage_chooseAgeGroups')}
              value={isEditing ? selectedAgeGroups[0] || '' : selectedAgeGroups} // сразу в value
              onChange={e =>
                setSelectedAgeGroups(isEditing ? [e.target.value] : e.target.value) // сразу в onChange
              }
              options={ageGroups.map(item => ({
                code: item.code,
                ru_name: item.name,
                kk_name: item.name
              }))}
            />
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography>{t('menuPage_addToMenu')}</Typography>
          <Box>
            <FormControlLabel
              control={<Checkbox checked={mealTypes.dinner} onChange={handleMealCheckboxChange} name="dinner" />}
              label={t('menu_dinner')}
            />
            <FormControlLabel
              control={<Checkbox checked={mealTypes.lateDinner} onChange={handleMealCheckboxChange} name="lateDinner" />}
              label={t('menu_lateDinner')}
            />
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} mt={2}>
          <Typography variant="h6" gutterBottom>
            {t('menuPage_fillingTheMenu')}
          </Typography>

          {!isEditing && (
            <Button
              variant="outlined"
              color="primary"
              onClick={recentMenuDialog.open}
            >
              {t('menuPage_selectFromRecent')}
            </Button>
          )}
        </Box>

        <Card variant="outlined" style={{ marginBottom: 20 }}>
          <CardContent>
            <Tabs
              value={activeDay}
              onChange={(e, v) => {
                setActiveDay(v);
                setActiveMealTab(0);
              }}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {days.map((d, i) => (
                <Tab key={i} label={formatDayLabel(d)} />
              ))}
            </Tabs>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Tabs
              value={activeMealTab}
              onChange={(e, v) => setActiveMealTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {mealKeys.map(key => (
                <Tab label={mealsLabels[key]} key={key} />
              ))}
            </Tabs>

            <Box mt={2}>
              {currentMeals[mealKeys[activeMealTab]].map((dish, index) => (
                <Box key={index} display="flex" alignItems="center" mb={1}>
                  <TextField
                    label={`${t('menuPage_dish')} ${index + 1}`}
                    variant="outlined"
                    fullWidth
                    value={dish}
                    onChange={e => {
                      const updated = [...mealsByDay];
                      updated[activeDay] = { ...updated[activeDay] };
                      updated[activeDay][mealKeys[activeMealTab]][index] = e.target.value;
                      setMealsByDay(updated);
                    }}
                  />
                  <Box ml={1}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        const updated = [...mealsByDay];
                        updated[activeDay] = { ...updated[activeDay] };
                        updated[activeDay][mealKeys[activeMealTab]] = updated[activeDay][mealKeys[activeMealTab]].filter((_, i) => i !== index);
                        setMealsByDay(updated);
                      }}
                    >
                      {t('menuPage_delete')}
                    </Button>
                  </Box>
                </Box>
              ))}

              <Button variant="contained" color="primary" onClick={handleAddDish}>
                + {t('menuPage_addDish')}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card variant="outlined" style={{ marginTop: 20 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('menuPage_reviewMenu')}
            </Typography>
            <Box style={{ marginTop: 20 }}>
              <StaticTablePagination
                columns={menuAddEditColumns({ t, mealTypes })}
                data={tableData}
                sortable={false}
                totalElements={tableData.length}
                loading={false}
                onClickLoadMore={() => {}}
              />
            </Box>


          </CardContent>
        </Card>

        {recentMenuDialog.isOpen && (
          <MenuRecentListDialog
            open={recentMenuDialog.isOpen}
            close={recentMenuDialog.close}
            onSelect={dataFromRecentList => {
              let templateMenu;
              if (Array.isArray(dataFromRecentList)) {
                templateMenu = dataFromRecentList[0];
              } else {
                templateMenu = dataFromRecentList;
              }
              const sourceMenuItems = templateMenu?.menuItems;
              if (!templateMenu || !sourceMenuItems || sourceMenuItems.length === 0) {
                recentMenuDialog.close();
                return;
              }
              setStartDate(templateMenu.startDate || '');
              setEndDate(templateMenu.endDate || '');
              setMenuId(null);
              const newMealsByDay = [...mealsByDay];
              let hasDinner = false;
              let hasLateDinner = false;
              const daysToClone = Math.min(10, sourceMenuItems.length);
              for (let i = 0; i < daysToClone; i++) {
                const srcDay = sourceMenuItems[i];
                newMealsByDay[i] = {
                  breakfast: srcDay.breakfast?.dish?.map(d => d.trim()) || [],
                  secondBreakfast: srcDay.secondBreakfast?.dish?.map(d => d.trim()) || [],
                  lunch: srcDay.lunch?.dish?.map(d => d.trim()) || [],
                  snack: srcDay.snack?.dish?.map(d => d.trim()) || [],
                  dinner: srcDay.dinner?.dish?.map(d => d.trim()) || [],
                  lateDinner: srcDay.lateDinner?.dish?.map(d => d.trim()) || []
                };
                if (newMealsByDay[i].dinner.length > 0) {
                  hasDinner = true;
                }
                if (newMealsByDay[i].lateDinner.length > 0) {
                  hasLateDinner = true;
                }
              }
              setMealsByDay(newMealsByDay);
              setMealTypes({
                dinner: hasDinner,
                lateDinner: hasLateDinner
              });
              recentMenuDialog.close();
            }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close} color="secondary">
          {t('menuPage_close')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={!isAllDaysFilled}
        >
          {t('menuPage_save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
